import { Box, Typography } from '@mui/material';
import { PeopleAltOutlined } from '@mui/icons-material';
import { useL10n } from '@uplift/hooks';
import { ChipMenu } from '@uplift/ui-v2';
import { useFindPsychiatristStyles } from './styles';
import clsx from 'clsx';
import { useCallback } from 'react';
import { MatchingProfileData } from '@uplift/api';

export type ProviderDetailsProps = {
  currentProvider: MatchingProfileData | null;
  provider: MatchingProfileData;
  onProviderSelect: (provider: MatchingProfileData | null) => void;
};

const ProviderDetails: React.VFC<ProviderDetailsProps> = ({
  currentProvider,
  provider,
  onProviderSelect
}) => {
  const { classes } = useFindPsychiatristStyles();
  const displayName = provider.nameDisplay?.defaultDisplay;

  return (
    <Box
      key={provider.user.id}
      className={clsx(
        classes.providerBox,
        { active: currentProvider?.user?.id === provider.user.id }
      )}
      onClick={() => onProviderSelect(provider)}
    >
      <Box className={classes.avatarContainer}>
        <img
          src={provider.avatarAsset?.url}
          alt={`avatar for ${displayName}`}
          className={classes.avatar}
        />
      </Box>
      <Box>
        {displayName}
      </Box>
    </Box>
  );
};

export type ViewByChipProps = {
  providers: MatchingProfileData[];
  currentProvider: MatchingProfileData | null;
  onProviderSelect: (provider: MatchingProfileData | null) => void;
};

export const ViewByChip: React.VFC<ViewByChipProps> = ({ providers, currentProvider, onProviderSelect }) => {
  const l10n = useL10n();

  const providerName = currentProvider
    ? currentProvider.nameDisplay?.defaultDisplay
    : l10n.findPsychiatrist.allProviders;

  const label = (
    <>
      <b>{l10n.findPsychiatrist.viewBy}</b>: {providerName}
    </>
  );

  return (
    <ChipMenu variant="outlined" color="primary" contentTitle={l10n.findPsychiatrist.viewByTitle} label={label}>
      <ViewBySelect providers={providers} currentProvider={currentProvider} onProviderSelect={onProviderSelect} />
    </ChipMenu>
  );
};

export type ViewBySelectProps = {
  providers: MatchingProfileData[];
  currentProvider: MatchingProfileData | null;
  onProviderSelect: (provider: MatchingProfileData | null) => void;
};

const ViewBySelect: React.VFC<ViewBySelectProps> = ({ providers, currentProvider, onProviderSelect }) => {
  const l10n = useL10n();
  const { classes } = useFindPsychiatristStyles();

  const handleClick = useCallback((provider: MatchingProfileData | null) => {
    onProviderSelect(provider);
  }, [ onProviderSelect ]);

  return (
    <Box>
      <Box
        className={clsx(
          classes.providerBox,
          { active: !currentProvider }
        )}
        onClick={() => handleClick(null)}
      >
        <Box className={classes.avatarContainer}>
          <Box className={clsx(classes.avatar, 'icon')}>
            <PeopleAltOutlined />
          </Box>
        </Box>
        <Box>
          <Typography variant="body1">
            {l10n.findPsychiatrist.allProviders}
          </Typography>
          <Typography color="textSecondary">
            {l10n.findPsychiatrist.timeBased}
          </Typography>
        </Box>
      </Box>
      {providers.map(provider => (
        <ProviderDetails
          key={provider?.user.id}
          currentProvider={currentProvider}
          provider={provider}
          onProviderSelect={onProviderSelect}
        />

      ))}
    </Box>
  );
};
